// NEVANU — Tab Router mit Dynamic Imports
// Ersetzt die loadDashboard() if/else-Kette schrittweise.
// Neue Feature-Module werden als lazy-loaded TypeScript-Module registriert.
// Legacy-Module (noch in form.js) werden über die globale Funktion aufgerufen.

export interface RouteHandler {
  load: (container: HTMLElement) => void | Promise<void>;
  unload?: () => void;
}

/** Registrierte Feature-Module (TypeScript) */
const routes = new Map<string, () => Promise<RouteHandler>>();

/** Aktuell geladenes Modul */
let currentRoute: { name: string; handler?: RouteHandler } | null = null;

/**
 * Feature-Modul registrieren (lazy-loaded)
 *
 * Beispiel:
 *   registerRoute('calculator', () => import('../features/calculator/calculator'));
 */
export function registerRoute(name: string, loader: () => Promise<RouteHandler>): void {
  routes.set(name, loader);
}

/**
 * Zu einem Modul navigieren
 * Prüft zuerst ob ein TypeScript-Modul registriert ist,
 * falls nicht wird die Legacy-Funktion aus form.js aufgerufen.
 */
export async function navigateTo(tab: string, container: HTMLElement): Promise<void> {
  // Altes Modul entladen
  if (currentRoute?.handler?.unload) {
    try {
      currentRoute.handler.unload();
    } catch (e) {
      console.warn(`[Router] Fehler beim Entladen von ${currentRoute.name}:`, e);
    }
  }

  // Neues TypeScript-Modul?
  if (routes.has(tab)) {
    try {
      const loader = routes.get(tab)!;
      const handler = await loader();
      currentRoute = { name: tab, handler };
      await handler.load(container);
      console.log(`[Router] Modul "${tab}" geladen (TypeScript)`);
      return;
    } catch (e) {
      console.error(`[Router] Fehler beim Laden von "${tab}":`, e);
      // Fallback: Legacy-Funktion direkt versuchen bevor Fehlermeldung gezeigt wird
      // Alle bekannten Legacy-Funktionen als Fallback wenn TS-Modul crasht
      const legacyMap: Record<string, string> = {
        home: 'loadHomeScreen',
        kalender: 'loadKalender',
        kunden: 'loadKunden',
        einstellungen: 'loadEinstellungenView',
        protokolle: 'loadProtokolle',
        kollegen: 'loadKollegenView',
        terminliste: 'loadTerminliste',
        arbeitszeiten: 'loadArbeitszeitenAdmin',
        kampagnen: 'loadKampagnen',
        aufgaben: 'loadAufgaben',
        team: 'loadTeamManager',
        neukunden: 'loadNeukundenList',
        export: 'loadExportPanel',
        bestellungen: 'loadBestellungen',
        news: 'loadNewsPage',
        zeiterfassung: 'loadZeiterfassung',
        lager: 'loadLagerView',
        profil: 'loadProfilView',
        projekt: 'loadProjektBoard',
        medien: 'loadMedien',
        berichte: 'loadBerichte',
        learning: 'loadLearning',
      };
      const fnName = legacyMap[tab];
      if (fnName && typeof (window as any)[fnName] === 'function') {
        console.log(`[Router] Fallback: ${fnName}() direkt aufrufen`);
        (window as any)[fnName](container);
        currentRoute = { name: tab };
        return;
      }
      container.innerHTML = `
        <div style="text-align:center;padding:60px 20px;color:var(--gray)">
          <span class="material-symbols-outlined" style="font-size:48px;color:#e74c3c" data-symbol="error">&#xe000;</span>
          <p style="margin-top:16px">Modul "${tab}" konnte nicht geladen werden.</p>
          <button onclick="loadDashboard('home')" style="margin-top:12px;padding:10px 20px;border-radius:50px;border:none;background:var(--nv-akzent);color:#fff;cursor:pointer">
            Zurück zum Dashboard
          </button>
        </div>`;
      return;
    }
  }

  // Fallback: Legacy-Funktion aus form.js
  currentRoute = { name: tab };
  const legacyFallback: Record<string, string> = {
    home: 'loadHomeScreen',
    aufgaben: 'loadAufgaben',
    protokolle: 'loadProtokolle',
    team: 'loadTeamManager',
    neukunden: 'loadNeukundenList',
    export: 'loadExportPanel',
    bestellungen: 'loadBestellungen',
    news: 'loadNewsPage',
    zeiterfassung: 'loadZeiterfassung',
    lager: 'loadLagerView',
    kollegen: 'loadKollegenView',
    profil: 'loadProfilView',
    kalender: 'loadKalender',
    terminliste: 'loadTerminliste',
    kunden: 'loadKunden',
    leistungen: 'loadLeistungenAdmin',
    arbeitszeiten: 'loadArbeitszeitenAdmin',
    einstellungen: 'loadEinstellungenView',
    projekt: 'loadProjektBoard',
    kampagnen: 'loadKampagnen',
    medien: 'loadMedien',
    berichte: 'loadBerichte',
    learning: 'loadLearning',
  };
  const fn = legacyFallback[tab];
  if (fn && typeof (window as any)[fn] === 'function') {
    (window as any)[fn](container);
  }
}

/**
 * Aktuellen Tab-Namen holen
 */
export function getCurrentTab(): string {
  return currentRoute?.name || 'home';
}
